import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import {EventConstants} from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {
  Protein,
  ProteinVersion,
  ProteinAnnotationVersion,
} from 'molbio/aa-sequences/util/AaSequenceConstants';
import * as ProteinProperties from 'molbio/aa-sequences/util/AaSequenceProperties';
import * as sequtil from 'molbio/SeqUtil';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as VersionHistoryUtil from 'util/VersionHistoryUtil';

export interface SearchResult {
  index: number;
  aminoAcids: string;
  proteinId: string;
  isSelected?: boolean;
}

class AaSequenceStore extends Store {
  proteinsById!: {[proteinId: string]: Protein};
  _versionsById!: {[proteinId: string]: Array<ProteinVersion>};
  _proteinIdsForSearchRefCount!: {[proteinId: string]: number};
  _pendingOpenSearchView!: boolean;
  searchQuery!: string | null;
  searchResults!: Array<SearchResult>;
  _currentPredictionIdByProteinVersion!: {[key: number]: string | null};

  override resetState(): void {
    this.proteinsById = {};
    this._versionsById = {};
    this._proteinIdsForSearchRefCount = {};
    this._pendingOpenSearchView = false;
    this.searchQuery = null;
    this.searchResults = [];
    this._currentPredictionIdByProteinVersion = {};
  }

  getAaSequenceById(proteinId: string): Protein | undefined {
    return this.proteinsById[proteinId];
  }

  _setAaSequence(proteinId: string, protein: Protein): void {
    this.proteinsById[proteinId] = protein;
  }

  getVersionsById(proteinId: string): Array<ProteinVersion> {
    return this._versionsById[proteinId];
  }

  getLatestVersionId(proteinId: string): number {
    if (!this._versionsById[proteinId]) {
      // If we haven't loaded the versions yet (which should be rare), just use the current version
      // as the latest version, since it's the latest that we currently know about.
      return this.proteinsById[proteinId].version.id;
    }
    let maxVersion = -1;
    for (const version of this._versionsById[proteinId]) {
      maxVersion = Math.max(maxVersion, version.id);
    }
    return maxVersion;
  }

  getPendingOpenSearchView(): boolean {
    return this._pendingOpenSearchView;
  }

  getAnnotation(proteinId: string, annotationId: string): ProteinAnnotationVersion | undefined {
    return _.findWhere(this.proteinsById[proteinId].version.annotations, {
      id: annotationId,
    });
  }

  getAnnotationAminoAcids(proteinId: string, annotationId: string): string {
    const annotation = this.getAnnotation(proteinId, annotationId)!;
    const fullAminoAcids = this.proteinsById[proteinId].version.aminoAcids;
    return sequtil.residueSlice(fullAminoAcids, annotation.start, annotation.end);
  }

  /**
   * Finds the version to revert to when doing an undo, or null if there is no previous version to
   * revert to. Generally, this is just a matter of walking one level up the tree, but if we are
   * already on a revert commit, then we are logically at our parent, so we should move to our
   * grandparent.
   */
  getUndoVersionId(proteinId: string): number | null {
    const currentVersion = this.proteinsById[proteinId].version;
    return VersionHistoryUtil.getVersionIdToResetTo(currentVersion, this._versionsById[proteinId]);
  }

  _setVersions(proteinId: string, versions: Array<ProteinVersion>): void {
    this._versionsById[proteinId] = versions;
  }

  getSearchResults(proteinId: string): Array<SearchResult> {
    return _.where(this.searchResults, {proteinId});
  }
  getNumSearchResults(): number {
    return this.searchResults.length;
  }
  getSelectedSearchResultIndex(): number {
    // eslint-disable-next-line no-restricted-syntax
    return _.findIndex(this.searchResults, ({isSelected}) => isSelected);
  }
  getSelectedSearchResult(): SearchResult | undefined {
    // eslint-disable-next-line no-restricted-syntax
    return _.find(this.searchResults, ({isSelected}) => isSelected);
  }

  getCoordinateStructureId(versionId: number): string | null {
    return this._currentPredictionIdByProteinVersion[versionId] || null;
  }

  setCoordinateStructureId(versionId: number, coordinateStructureId: string | null): void {
    this._currentPredictionIdByProteinVersion[versionId] = coordinateStructureId;
  }

  _searchAminoAcids(proteinId: string, query: string): Array<SearchResult> {
    // Treat asterisks as stop codons, and Xs as arbitrary amino acids. This matches how we do
    // auto annotation, and should be kept in sync with benchling/lib/proteins::auto_annotate_protein. We
    // are nice and strip spaces since users may copy and paste from other places, which also matches how
    // we handle DNA sequences.
    const EXTRA_ALLOWED_CHARACTERS = 'X* ';
    const isValidQuery = query
      .split('')
      .every(
        (aminoAcid) =>
          ProteinProperties.areAminoAcidsValid(aminoAcid) || EXTRA_ALLOWED_CHARACTERS.includes(aminoAcid)
      );

    // If the query has invalid characters, just return zero results.
    if (!isValidQuery) {
      return [];
    }

    const regexQuery = query.replace(/\*/g, '$').replace(/X/gi, '.').replace(/ /g, '');

    const regex = new RegExp(`(?=(${regexQuery}))`, 'gi');
    const {aminoAcids} = this.getAaSequenceById(proteinId)!.version;
    const results = [];
    let result = regex.exec(aminoAcids);
    while (result) {
      results.push({
        index: result.index,
        aminoAcids: result[1],
        proteinId,
      });
      regex.lastIndex = result.index + 1;
      result = regex.exec(aminoAcids);
    }
    return results;
  }

  addProteinsForSearch(proteinId: string): void {
    this._proteinIdsForSearchRefCount = reactUpdate(
      this._proteinIdsForSearchRefCount,
      _.object([[proteinId, {$apply: (count: number) => (count || 0) + 1}]])
    );
  }

  removeProteinsForSearch(proteinId: string): void {
    if (
      this._proteinIdsForSearchRefCount[proteinId] != null &&
      this._proteinIdsForSearchRefCount[proteinId] > 1
    ) {
      this._proteinIdsForSearchRefCount = reactUpdate(
        this._proteinIdsForSearchRefCount,
        _.object([
          [
            proteinId,
            {
              $apply(count: number) {
                return count - 1;
              },
            },
          ],
        ])
      );
    } else {
      // eslint-disable-next-line no-restricted-syntax
      this._proteinIdsForSearchRefCount = _.omit(this._proteinIdsForSearchRefCount, proteinId);
    }
    this.searchResults = this.searchResults.filter((result) => result.proteinId !== proteinId);
  }

  _getDisplaySelectionRange(
    proteinId: string,
    startIndex: number,
    endIndex: number | null
  ): {displayStart: string; displayEnd: string} {
    const aaSequence = this.proteinsById[proteinId];
    const displayStartNumber = sequtil.modelStartToDisplay(startIndex, 1);
    const displayStartString = displayStartNumber == null ? '' : displayStartNumber.toString();
    const displayEndNumber = sequtil.modelEndToDisplay(endIndex, aaSequence.version.aminoAcids.length, 1);
    const displayEndString = displayEndNumber == null ? '' : displayEndNumber.toString();
    return {displayStart: displayStartString, displayEnd: displayEndString};
  }

  _recomputeSearchResults(): void {
    if (this.searchQuery == null) {
      this.searchResults = [];
      return;
    }
    const proteinIds = Object.keys(this._proteinIdsForSearchRefCount);
    // eslint-disable-next-line no-restricted-syntax
    const newSearchResults = _.flatten(
      proteinIds.map((proteinId) => this._searchAminoAcids(proteinId, this.searchQuery!))
    );
    if (newSearchResults.length > 0) {
      newSearchResults[0].isSelected = true;
    }
    this.searchResults = newSearchResults;
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.PROTEIN_OPEN_COMPLETED: {
        const {protein, proteinId} = action;
        assert(protein.id === proteinId, '');
        this._setAaSequence(proteinId, protein);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_MERGE: {
        const aaSequence = this.getAaSequenceById(action.proteinId)!;
        const newAaSequence = reactUpdate(aaSequence, {$merge: action.protein});
        this._setAaSequence(action.proteinId, newAaSequence);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_UPDATE_COMPLETED: {
        const {newProtein, proteinId} = action;
        assert(newProtein.id === proteinId, '');
        this._setAaSequence(proteinId, newProtein);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_AA_UPDATE_COMPLETED: {
        const {newProtein, proteinId} = action;
        assert(newProtein.id === proteinId, '');
        this._setAaSequence(proteinId, newProtein);
        this._recomputeSearchResults();
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_FETCH_VERSIONS_COMPLETED: {
        const {proteinId, versions} = action;
        this._setVersions(proteinId, versions);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_SEARCH_OPEN_VIEW: {
        this._pendingOpenSearchView = true;
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_SEARCH_OPEN_VIEW_COMPLETED: {
        this._pendingOpenSearchView = false;
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_SEARCH_AMINO_ACIDS: {
        this.searchQuery = action.query;
        this._recomputeSearchResults();
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ADD_FOR_SEARCH: {
        for (let i = 0; i < action.proteinIds.length; i++) {
          const proteinId = action.proteinIds[i];
          this.addProteinsForSearch(proteinId);
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_REMOVE_FROM_SEARCH: {
        for (let j = 0; j < action.proteinIds.length; j++) {
          const proteinId = action.proteinIds[j];
          this.removeProteinsForSearch(proteinId);
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_VIEW_NEXT_SEARCH_RESULT: {
        const selectedIndex = this.getSelectedSearchResultIndex();
        if (selectedIndex < this.searchResults.length - 1 && selectedIndex !== -1) {
          this.searchResults = reactUpdate(
            this.searchResults,
            _.object([
              [selectedIndex, {$merge: {isSelected: false}}],
              [selectedIndex + 1, {$merge: {isSelected: true}}],
            ])
          );
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_VIEW_PREVIOUS_SEARCH_RESULT: {
        const selectedIndex = this.getSelectedSearchResultIndex();
        if (selectedIndex > 0) {
          this.searchResults = reactUpdate(
            this.searchResults,
            _.object([
              [selectedIndex, {$merge: {isSelected: false}}],
              [selectedIndex - 1, {$merge: {isSelected: true}}],
            ])
          );
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_SELECT_SEARCH_RESULT: {
        const selectedIndex = this.getSelectedSearchResultIndex();
        const targetIndex = this.searchResults.findIndex(
          ({proteinId, index, aminoAcids}) =>
            proteinId === action.proteinId && index === action.index && aminoAcids === action.aminoAcids
        );
        if (targetIndex !== -1 && targetIndex !== selectedIndex) {
          this.searchResults = reactUpdate(
            this.searchResults,
            _.object([
              [selectedIndex, {$merge: {isSelected: false}}],
              [targetIndex, {$merge: {isSelected: true}}],
            ])
          );
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_CLEAR_SEARCH_RESULTS: {
        this.searchResults = [];
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_SET_CURRENT_COORDINATE_STRUCTURE_ID: {
        const {versionId, coordinateStructureId} = action;
        this.setCoordinateStructureId(versionId, coordinateStructureId);
        this.emitChange();
        break;
      }
      case EventConstants.FILE_BROWSER_RENAME_FILE: {
        const {id, name, displayLabel} = action;
        const aaSequence = this.getAaSequenceById(id);
        if (aaSequence) {
          const newAaSequence = reactUpdate(aaSequence, {$merge: {name, displayLabel}});
          this._setAaSequence(id, newAaSequence);
          this.emitChange();
        }
        break;
      }
      case EventConstants.FILE_BROWSER_MOVE_FILES_COMPLETED: {
        const {ids, folder} = action;
        let hasChanged = false;
        // eslint-disable-next-line no-restricted-syntax
        _.each(ids, (id: string) => {
          const aaSequence = this.getAaSequenceById(id);
          if (aaSequence) {
            let newAaSequence = aaSequence;
            if (RegistryUtil.isRegistry(folder!.source)) {
              newAaSequence = reactUpdate(newAaSequence, {
                $merge: {projectFolderId: aaSequence.folder.api_identifier},
              });
            }
            newAaSequence = reactUpdate(newAaSequence, {$merge: {folder: folder!}});
            this._setAaSequence(id, newAaSequence);
            hasChanged = true;
          }
        });
        if (hasChanged) {
          this.emitChange();
        }
        break;
      }
      case EventConstants.FILE_BROWSER_MOVE_SHORTCUTS_COMPLETED: {
        const {ids, folder} = action;
        let hasChanged = false;
        // eslint-disable-next-line no-restricted-syntax
        _.each(ids, (id: string) => {
          const aaSequence = this.getAaSequenceById(id);
          if (aaSequence) {
            let newAaSequence = aaSequence;
            newAaSequence = reactUpdate(newAaSequence, {
              $merge: {projectFolderId: folder! && folder.api_identifier},
            });
            this._setAaSequence(id, newAaSequence);
            hasChanged = true;
          }
        });
        if (hasChanged) {
          this.emitChange();
        }
        break;
      }
      case EventConstants.FILE_BROWSER_UNREGISTER_FILES: {
        const {ids} = action;
        let hasChanged = false;
        // eslint-disable-next-line no-restricted-syntax
        _.each(ids, (id: string) => {
          const aaSequence = this.getAaSequenceById(id);
          if (aaSequence) {
            // @ts-expect-error: should probably use undefined
            const newAaSequence = reactUpdate(aaSequence, {$merge: {projectFolderId: null}});
            this._setAaSequence(id, newAaSequence);
            hasChanged = true;
          }
        });
        if (hasChanged) {
          this.emitChange();
        }
        break;
      }
      default:
        break;
    }

    return true;
  }
}

const aaSequenceStore = new AaSequenceStore(AppDispatcher);
export default aaSequenceStore;
